import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Link } from "react-router-dom"
import { LogOut, User, Shield, BookOpen, Loader2, UserCog, KeyRound, KeySquare, Settings, SunMoon } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { useAuth } from "@/hooks/useAuth"
import { useTheme } from "@/hooks/useTheme"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { api } from "@/lib/api"
import ReactMarkdown from "react-markdown"
import { isAdmin } from "@/lib/roles"

export function HeaderUserProfile() {
  const { user, licenses, logout } = useAuth()
  const { theme, setTheme } = useTheme()
  const [changelogOpen, setChangelogOpen] = useState(false)

  const { data: versionData, isLoading: versionLoading } = useQuery({
    queryKey: ["version"],
    queryFn: () => api.version.get(),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })

  if (!user) {
    return null
  }

  const initials = user.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : user.email[0].toUpperCase()

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 rounded-lg hover:bg-muted transition-colors p-1">
            <Avatar className="h-8 w-8">
              {user.avatar && <AvatarImage src={user.avatar} alt={user.name} />}
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                {initials}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[240px]">
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">{user.name || user.email}</p>
              <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled>
            <User className="mr-2 h-4 w-4" />
            <span className="flex-1">Role: {user.role}</span>
          </DropdownMenuItem>
          {isAdmin(user) && (
            <DropdownMenuItem disabled>
              <Shield className="mr-2 h-4 w-4 text-primary" />
              <span className="text-primary">Admin Access</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {/* Settings that belong to the person, not to a client site, so they
              are reachable from every workspace. The pages stay under
              /settings; the sidebar's Settings link is SEO-only. */}
          <DropdownMenuItem asChild className="cursor-pointer">
            <Link to="/settings/account">
              <UserCog className="mr-2 h-4 w-4" />
              <span className="flex-1">Account</span>
            </Link>
          </DropdownMenuItem>
          {(isAdmin(user) || user.api_access_enabled) && (
            <DropdownMenuItem asChild className="cursor-pointer">
              <Link to="/settings/api-tokens">
                <KeySquare className="mr-2 h-4 w-4" />
                <span className="flex-1">For LLMs</span>
              </Link>
            </DropdownMenuItem>
          )}
          {licenses.length > 0 && (
            <DropdownMenuItem asChild className="cursor-pointer">
              <Link to="/settings/license">
                <KeyRound className="mr-2 h-4 w-4" />
                <span className="flex-1">Licenses</span>
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="cursor-pointer">
              <SunMoon className="mr-2 h-4 w-4" />
              <span className="flex-1">Theme</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={theme} onValueChange={(next) => setTheme(next as typeof theme)}>
                <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="system">System</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem asChild className="cursor-pointer">
            <Link to="/settings">
              <Settings className="mr-2 h-4 w-4" />
              <span className="flex-1">Client & site settings</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setChangelogOpen(true)} className="cursor-pointer">
            <BookOpen className="mr-2 h-4 w-4" />
            <span className="flex-1">Changelog</span>
            <Badge variant="secondary" className="ml-2">v{versionData?.version || "..."}</Badge>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive focus:text-destructive">
            <LogOut className="mr-2 h-4 w-4" />
            <span>Logout</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={changelogOpen} onOpenChange={setChangelogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <DialogTitle>Changelog</DialogTitle>
              <Badge variant="secondary">v{versionData?.version || "1.0.0"}</Badge>
            </div>
            <DialogDescription>
              Version history and release notes
            </DialogDescription>
          </DialogHeader>

          {versionLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto pr-4">
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown>{versionData?.changelog || "No changelog available."}</ReactMarkdown>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
